import * as React from 'react';
import classnames from 'classnames';
import {FormattedMessage} from 'react-intl';

import {Showing as ShowingType} from "features/search/types";
import {LinkObject, RealEstateAgent} from "features/template/viewModels/ResidenceStage";

import './_residenceAside.scss';

import {P} from 'components/atoms/Text/P';
import {ButtonLink} from 'components/atoms/LinksAndButtons/ButtonLink';
import {LinkList} from 'components/molecules/Lists/LinkList';
import {Showings} from 'components/molecules/Showings/Showings';
import {ContactCard} from 'components/molecules/ContactCard';
import { ButtonColor, ButtonSize, ButtonType } from '@atoms/LinksAndButtons/Button';
import { hasShowing, showingIsFuture } from 'features/services/utils/showing-helpers';
import { useAuth } from 'features/authentication/hooks/useAuth';
import {Margin, HeadlineLevel} from 'features/ui/enums';
import {Headline} from '@atoms/Text/Headline';

interface Props {
    className?: string;

    hasShowings?: boolean;
    showings?: ShowingType[];
    stageName?: string;
    stageId?: string;
    stageStatus: string;
    isVip?: boolean;
    isLoggedIn?: boolean;
    smarta?: boolean;
    showingText?: string;
    showingLinkText?: string;
    showingLinkUrl?: string;

    tennantLinkUrl?: string;
    contacts?: RealEstateAgent[];
    links?: LinkObject[];

    rentalProjectName?: string;
}

export const ResidenceAside: React.FunctionComponent<Props> = (props) => {
    const _renderCenteredContactCards = props.contacts && props.contacts.filter((agent) => agent.imageUrl !== '' && agent.imageUrl !== null).length === 0;
    const asideClass = classnames('project-information ' + props.className, {});
    const {user: _user, isLoggedIn: _isLoggedIn} = useAuth();
    
    const today = new Date();
    let filteredShowings: ShowingType[] = [];

    if (props.showings) {
        filteredShowings = props.showings.filter(x => showingIsFuture(x));

        filteredShowings = filteredShowings.filter(x => new Date(x.endDate) > today).sort(function (a, b) {
            if (a.startDate > b.startDate) return 1;
            if (a.startDate < b.startDate) return -1;
            return 0;
        });
    }

    return (
        <aside className={asideClass}>
            {hasShowing(props.showings, props.stageStatus, props.showingText) &&
                <Showings
                    showings={filteredShowings}
                    stageName={props.stageName ? props.stageName : ''}
                    stageId={props.stageId ? props.stageId : ''}
                    stageStatus={props.stageStatus}
                    smarta={props.smarta}
                    showingText={props.showingText}
                    showingLinkText={props.showingLinkText}
                    showingLinkUrl={props.showingLinkUrl}
                />
            }

            {props.tennantLinkUrl &&
                <div className="project-information__tentant">
                    <Headline level={HeadlineLevel.H2} darkBg margin={Margin.L}>Hyresgäst?</Headline>
                    <P>
                        Här hittar du som hyresgäst information om din hyresrätt i {props.rentalProjectName}.
                    </P>
                    <ButtonLink 
                        href={props.tennantLinkUrl} 
                        buttonColor={ButtonColor.LIGHT} 
                        buttonSize={ButtonSize.WIDE} 
                        buttonType={ButtonType.DEFAULT} 
                    >
                        Läs mer
                    </ButtonLink>
                </div>
            }

            {(props.contacts && props.contacts.length > 0) &&
                <div className="contacts">
                    <Headline level={HeadlineLevel.H2} margin={Margin.L}><FormattedMessage id='jm.components.StagePage.boxContact' /></Headline>

                    <div className="contacts__list">
                        {props.contacts && props.contacts.map((agent, index) => {
                            return (
                                <ContactCard
                                    level={HeadlineLevel.H3}
                                    img={agent.imageUrl}
                                    name={agent.name}
                                    title={agent.title}
                                    company={agent.company}
                                    phone={agent.phone}
                                    mail={agent.email}
                                    key={`real-agent-${index}`}
                                />
                            );
                        })}
                    </div>
                </div>
            }

            {props.links && props.links !== null &&
                <div className="project-links">
                    <Headline level={HeadlineLevel.H2} margin={Margin.L}><FormattedMessage id='jm.components.StagePage.boxInfo' /></Headline>
                    <LinkList
                        links={props.links}
                        externalLinksIconFirst
                    />
                </div>
            }
        </aside>
    )
};

